import { useEffect, useState } from "react";
import { styled } from "styled-components";
import { CheckboxField, FormFieldLayout, Text } from "@wayflyer/flyui";

export interface Account {
  id: string;
  label: string;
  selected: boolean;
  properties?: Account[];
}

const SelectAllCheckboxContainer = styled.div`
  padding-bottom: var(--sizes-spacing-4);
  margin-bottom: var(--sizes-spacing-4);
  position: relative;
  :after {
    content: "";
    display: block;
    box-sizing: border-box;
    position: absolute;
    left: calc(var(--sizes-spacing-6) * -1);
    bottom: 0;
    width: calc(100% + var(--sizes-spacing-12));
    border-bottom: 1px solid var(--palette-border-neutral-subtle);
  }
`;

export const Spacer = styled.div`
  height: var(--sizes-spacing-1);
`;

export const AccountsForm = ({
  accounts,
  setAccounts,
  selectAllLabel,
}: {
  accounts: Account[];
  setAccounts;
  selectAllLabel: string;
}) => {
  const isEveryAccountSelected = accounts.every((account) =>
    account.properties ? account.properties?.every((prop) => prop.selected) : account.selected
  );

  const isNoAccountSelected = accounts.every((account) =>
    account.properties ? account.properties?.every((prop) => !prop.selected) : !account.selected
  );

  const hasAccountsWithMultipleProperties = accounts.some((ac) => ac.properties && ac.properties.length > 1);

  const [isAllChecked, setIsAllChecked] = useState(isEveryAccountSelected);
  const updateSelected = (account) => setAccounts([...new Set([...accounts, account])]);

  const selectAll = (selected) => {
    const newAccounts = accounts.map((ac) => {
      let accountWithProperties;
      if (ac.properties) {
        accountWithProperties = ac.properties.map((prop) => (prop.selected = selected));
      }
      return ac.properties ? { ...ac, accountWithProperties } : { ...ac, selected };
    });
    setAccounts([...new Set([...newAccounts])]);
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    setIsAllChecked(isEveryAccountSelected);
  }, [isEveryAccountSelected]);

  return (
    <>
      {(accounts.length > 1 || hasAccountsWithMultipleProperties) && (
        <SelectAllCheckboxContainer>
          <CheckboxField
            name="account-property-all-field"
            options={{
              name: "account-property-all",
              label: selectAllLabel,
              defaultChecked: isEveryAccountSelected,
              indeterminate: !isEveryAccountSelected && !isNoAccountSelected,
              checked: isAllChecked,
              onChange: (checked) => {
                setIsAllChecked(checked);
                selectAll(checked);
              },
            }}
          />
        </SelectAllCheckboxContainer>
      )}
      <FormFieldLayout>
        {accounts.map((ac, idx, arr) => {
          if (!Array.isArray(ac.properties)) {
            return (
              <CheckboxField
                name={`account-property-${idx}-field`}
                key={idx}
                options={{
                  name: `account-property-${idx}`,
                  label: ac.label,
                  defaultChecked: !!ac.selected,
                  checked: ac.selected,
                  onChange: (checked) => {
                    ac.selected = checked;
                    updateSelected(ac);
                  },
                }}
              />
            );
          } else {
            const prevAc = idx > 0 ? arr[idx - 1] : undefined;
            return (
              <FormFieldLayout key={idx}>
                {prevAc && !Array.isArray(prevAc?.properties) ? <Spacer /> : null}
                <Text>{ac.label}</Text>
                {ac.properties.map((pr, subIdx) => {
                  return (
                    <CheckboxField
                      key={subIdx}
                      name={`account-property-${idx}-${subIdx}-field`}
                      options={{
                        name: `account-property-${idx}-${subIdx}`,
                        label: pr.label,
                        defaultChecked: !!pr.selected,
                        checked: ac.properties && ac.properties[subIdx].selected,
                        onChange: (checked) => {
                          if (ac.properties) {
                            ac.properties[subIdx].selected = checked;
                            updateSelected(ac);
                          }
                        },
                      }}
                    />
                  );
                })}
                <Spacer />
              </FormFieldLayout>
            );
          }
        })}
      </FormFieldLayout>
    </>
  );
};
